<script>
import { mapState } from 'vuex';
export default{
  name: "TemplateCategories",
  computed: {
    ...mapState('whatsapp', ['currentCategory', 'selectedMessageType']),
    currentPreview() {
      return this.templatePreviews[this.currentCategory]?.[this.selectedMessageType];
    }
  },
  watch: {
    currentCategory(newVal, oldVal){
      this.$store.commit('whatsapp/setSelectedMessageType', this.messageTypes[newVal][0].name);
    }
  },
  data(){
    return{
      categories: [
        { name: "Marketing", value: "marketing", description: "Marketing is used for promotions or information about your business, products or services, or any message that isn't for utility or authentication purposes. E.g. welcome messages, newsletters, offers, coupons, catalogues, new shop opening hours." },
        { name: "Utilities", value: "utilities", description: "Utility is used for transaction, account, order or customer request. E.g. order confirmations, account updates, receipts, appointment reminders, billing. (Choose Marketing if you want to include promotion in the Utility message.)" },
        { name: "Authentication", value: "authentication", description: "Authentication is used for one-off passwords that your customers use to authenticate a transaction or login. E.g. one-off password, account recovery code." }
      ],
      messageTypes: {
        "marketing": [
          {
            name: "Custom",
            description: "Send promotions or announcements to increase awareness and engagement."
          },
          {
            name: "Catalogue",
            description: "Send messages about your entire catalogue or multiple products from it."
          },
          {
            name: "Flows",
            description: "Send a form to capture customer interests, appointment requests or run surveys."
          }
        ],
        "utilities": [
          {
            name: "Custom",
            description: "Send messages about an existing order or account."
          },
          {
            name: "Flows",
            description: "Send a form to collect feedback, send reminders or manage orders."
          },
        ],
        "authentication": [
          {
            name: "One-time passcode",
            description: "Send codes to verify a transaction or login."
          }
        ]
      },
      templatePreviews: {
        "marketing": {
          "Custom": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/yN/r/v8ib9s0JOMo.png",
            functionality: "Welcome messages, promotions, offers, coupons, newsletters, announcements",
            customizables: "Header, body, footer, button",
          },
          "Catalogue": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/y1/r/xJdfTT0gV68.gif",
            functionality: "Product/service discovery, sales, offers, targeted marketing, personalised shopping",
            customizables: "Catalogue, body",
          },
          "Flows": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/yz/r/4H6zSr_9q2A.gif",
            functionality: "Sign-ups, promotions, surveys, appointments",
            customizables: "Form, body, button",
          }
        },
        "utilities": {
          "Custom": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/yk/r/Czm3vxesN-O.png",
            functionality: "Order confirmations, account update, receipts, appointment reminders, billing",
            customizables: "Header, body, footer, button",
          },
          "Flows": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/yZ/r/kQTcoVBAOiV.gif",
            functionality: "SCustomer support, surveys",
            customizables: "Form, body, button",
          }
        },
        "authentication": {
          "One-time passcode": {
            imageUrl: "https://static.xx.fbcdn.net/rsrc.php/v4/yL/r/vwkjuR20nRH.png",
            functionality: "One-time password, account recovery code, account verification, integrity challenges",
            customizables: "Code delivery method",
          }
        }
      },
    }
  }
}
</script>

<template>
  <div class="flex flex-wrap">
    <div class="flex-1 rounded-md bg-white p-4">
      <h1>Set up your template</h1>
      <p>
        Choose the category that best describes your message template. 
        Then, select the type of message that you want to send. Learn more about categories.
      </p>
      <div class="flex mb-2 border-2 rounded-md">
        <button 
          v-for="(category, index) in categories" 
          v-tooltip.right-end="{
            content: category.description,
            classes: ['whitespace-normal', 'max-w-[300px]', 'break-words'],
          }"
          :key="index" 
          @click="$store.commit('whatsapp/setCurrentCategory', category.value)"
          class="border flex-1 rounded-none"
          :class="{ 'bg-slate-500 text-white': currentCategory == category.value, 'hover:bg-slate-400 hover:text-white': currentCategory != category.value }"
        >
          <!-- <fluent-icon icon="checkmark-circle" size="12" /> -->
          {{ category.name }}</button>
      </div>
      <div class="flex flex-col gap-2">
        <div v-for="(type, index) in messageTypes[currentCategory]" 
          :key="index" 
          class="flex items-center gap-4 cursor-pointer rounded-md px-4 py-2" 
          :class="{'bg-woot-100': type.name == selectedMessageType, 'hover:bg-slate-200': type.name != selectedMessageType}"
          @click="$store.commit('whatsapp/setSelectedMessageType', type.name)">
          <span 
            v-if="type.name == selectedMessageType" 
            class="inline-block w-4 h-4 rounded-full border border-woot-500 bg-woot-500"></span>
          <span 
            v-else 
            class="inline-block w-4 h-4 rounded-full border border-slate-500"></span>
          <span>
            <h2>{{ type.name }}</h2>
            <p>{{ type.description }}</p>
          </span>
        </div>
      </div>
    </div>

    <div class="flex flex-col mx-4 w-[300px] min-w-[300px]">  
      <!-- Content remains the same -->
      <h1>Template Preview</h1>
      <img :src="currentPreview.imageUrl" class="w-[300px] h-[450px] rounded-xl object-cover my-4" alt="Image" />
      <div>
        <h5 class="text-xs">This template is good for</h5>
        <p class="text-xs text-wrap break-words">{{ currentPreview.functionality }}</p>
        <br>
        <h5 class="text-xs">Template areas that you can customise</h5>
        <p class="text-xs text-wrap break-words">{{ currentPreview.customizables }}</p>
      </div>
    </div>
  </div>
</template>